import React from "react";
import { useTranslation } from "react-i18next";
import Highlighter from "react-highlight-words";
import { ChevronRight, MoreVertical, Phone } from "lucide-react";
import { SINGLE_VALUE, TERMINALS_TO_SHOW, FIRST_LEVEL } from "./constants";
import SubMenu from "./SubMenu";
import TerminalItem from "./TerminalItem";
import { toggleBranch } from "./helpers";

const BranchItem = ({
  branch,
  outlet,
  region,
  expandedBranches,
  expandedOutlets,
  searchTerm,
  subMenuID,
  setSubMenuID,
  showAllTerminals,
  handleToggleShowAll,
  setCurrentBranch,
  setInfoPopupOpen,
  setExpandedBranches,
  branchActions,
  terminalActions,
}) => {
  const { t: translate } = useTranslation();
  const hasTerminals = !!branch?.terminals?.length;

  return (
    <div key={branch.id} className="pl-6 mt-4">
      <div className="flex justify-between items-center">
        <div
          data-testid={`branch-${branch.id}`}
          onClick={() =>
            hasTerminals &&
            toggleBranch({
              branchId: branch.id,
              setSubMenuID,
              setExpandedBranches,
            })
          }
          className="text-md font-semibold cursor-pointer flex items-center flex-1"
        >
          <span
            className={`transform transition-transform duration-300 dark:text-white ${
              expandedBranches[branch.id] ? "rotate-90" : "rotate-0"
            }`}
          >
            <ChevronRight size={20} />
          </span>
          <div className="flex items-baseline">
            <span className="ml-2 dark:text-white text-gray-800">
              <Highlighter
                highlightClassName="text-red-500 bg-transparent"
                searchWords={[searchTerm]}
                autoEscape={true}
                textToHighlight={branch.name}
              />
            </span>
            <span className="ml-2 text-xs text-gray-400">
              {branch.terminals.length} {translate("hierarchy.terminal")}
              {branch.terminals.length > SINGLE_VALUE ? "s" : ""}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 dark:text-white">
          <button
            data-testid={`branch-action-${branch.id}`}
            onClick={() => setSubMenuID(branch.id)}
            className="relative"
          >
            <MoreVertical size={20} />
            {subMenuID === branch.id &&
              subMenuID !== null &&
              expandedOutlets[outlet.id] && (
                <SubMenu
                  id={branch}
                  type="outlet"
                  addType="terminal"
                  actions={branchActions}
                  parent={outlet}
                  onClose={() => setSubMenuID(null)}
                />
              )}
          </button>
          {(!!branch.addresses.length || !!branch.contacts.length) && (
            <div
              data-testid={`branch-info-${branch.id}`}
              className="cursor-pointer dark:bg-gray-900 bg-gray-200 p-1 rounded hover:bg-gray-300"
              onClick={() => {
                setCurrentBranch(branch);
                setInfoPopupOpen(branch.id);
              }}
            >
              <Phone size={20} />
            </div>
          )}
        </div>
      </div>

      {expandedBranches[branch.id] && (
        <div
          data-testid="terminals"
          className={`ml-[11px] justify-between transition-all flex items-baseline border-l-2 border-gray-200 duration-500`}
        >
          {!!branch.terminals.length && (
            <div
              className="flex flex-col items-start px-5 mt-7 gap-4"
              key={branch.id}
            >
              <p className="font-semibold dark:text-white text-gray-700">
                {translate("hierarchy.terminals")}
                {":"}
              </p>
              <div className="grid grid-cols-4 gap-4 items-center px-5 pl-0 flex-1">
                {branch.terminals
                  .slice(
                    FIRST_LEVEL,
                    showAllTerminals[branch.id]
                      ? branch.terminals.length
                      : TERMINALS_TO_SHOW
                  )
                  .map((terminal) => (
                    <TerminalItem
                      key={terminal.id}
                      terminal={terminal}
                      branch={branch}
                      outlet={outlet}
                      branchId={branch.id}
                      searchTerm={searchTerm}
                      subMenuID={subMenuID}
                      setSubMenuID={setSubMenuID}
                      expandedBranches={expandedBranches}
                      actions={terminalActions}
                    />
                  ))}
              </div>
              {branch.terminals.length > TERMINALS_TO_SHOW && (
                <button
                  className="pb-4 text-gray-500 text-sm underline"
                  onClick={() => handleToggleShowAll(branch.id)}
                >
                  {showAllTerminals[branch.id] ? "Show Less" : "View All"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchItem;
